// ============================================================================
// MOCK API ADAPTER
// ----------------------------------------------------------------------------
// Installs a custom axios adapter on the app's axios instance so every API
// call resolves against local dummy data instead of the (absent) backend.
// Gated by DEMO_MODE. The route table is populated from ./mockRoutes.
// ============================================================================
import { DEMO_MODE } from './demoConfig';
import { ROUTES } from './mockRoutes';

function normalizePath(config: any): string {
    let url: string = config.url || '';
    // strip absolute origin if a full URL was passed
    url = url.replace(/^https?:\/\/[^/]+/i, '');
    // strip baseURL if present and relative kept it
    const base = config.baseURL || '';
    if (base && url.startsWith(base)) url = url.slice(base.length);
    // strip query string (params handled separately)
    url = url.split('?')[0];
    if (!url.startsWith('/')) url = '/' + url;
    return url;
}

function okResponse(config: any, body: any) {
    return {
        data: body,
        status: 200,
        statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config,
    };
}

async function mockAdapter(config: any) {
    const method = (config.method || 'get').toLowerCase();
    const path = normalizePath(config);

    for (const route of ROUTES) {
        if (route.method !== method) continue;
        const m = path.match(route.pattern);
        if (m) {
            let body;
            try {
                body = await route.handler({
                    params: m.slice(1),
                    query: config.params || {},
                    data: config.data ? safeParse(config.data) : undefined,
                    config,
                    path,
                });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[mockApi] handler error for', path, e);
                body = { success: false, data: null, error: String(e) };
            }
            // eslint-disable-next-line no-console
            console.log(`%c[mockApi] ${method.toUpperCase()} ${path}`, 'color:#7c5cff', body);
            return okResponse(config, body);
        }
    }

    // Unmatched: benign empty success so the UI shows empty states, not errors.
    // eslint-disable-next-line no-console
    console.log(`%c[mockApi] (unmatched) ${method.toUpperCase()} ${path}`, 'color:#999');
    return okResponse(config, { success: true, data: null });
}

function safeParse(d: any) {
    if (typeof d !== 'string') return d;
    try { return JSON.parse(d); } catch { return d; }
}

export function installMockApi(instance: any) {
    if (!DEMO_MODE) return;
    // axios reads the adapter from instance.defaults.adapter (not instance.adapter)
    instance.defaults.adapter = mockAdapter;
    instance.adapter = mockAdapter;
    // eslint-disable-next-line no-console
    console.log('%c[mockApi] installed on axios instance', 'color:#7c5cff;font-weight:bold');
}
